import type { PdfEngine } from '@embedpdf/models';
import { useEffect, useState } from 'react';

// Module-level singleton so WASM init (100-500ms) happens once per page
// load instead of per <PdfViewerHeadless> mount. EmbedPDF's own
// usePdfiumEngine re-creates the engine in each component's useEffect,
// which makes every Lightbox open pay the cold-start cost.
//
// The engine is intentionally kept alive for the page lifetime — we never
// destroy it. Worker + WASM heap are ~few MB; cheaper than the re-init.

type Engine = PdfEngine;

let enginePromise: Promise<Engine> | null = null;

function getEngine(): Promise<Engine> {
  if (!enginePromise) {
    enginePromise = (async () => {
      // TODO(docs-p2): self-host pdfium.wasm as a bundled asset instead of
      // fetching from jsdelivr at runtime. The CDN dependency breaks Phase 2
      // offline (docs is a primary offline consumer), adds a third-party
      // availability/privacy dependency, and the version below is hand-synced
      // to package.json (@embedpdf/pdfium) with no build-time check.
      // URL mirrors @embedpdf/engines/react default. Kept in sync manually
      // because the constant isn't exported.
      const wasmUrl = `https://cdn.jsdelivr.net/npm/@embedpdf/pdfium@2.14.2/dist/pdfium.wasm`;
      const { createPdfiumEngine } = await import(
        '@embedpdf/engines/pdfium-worker-engine'
      );
      return createPdfiumEngine(wasmUrl);
    })();
  }
  return enginePromise;
}

export function useSharedPdfiumEngine(): {
  engine: Engine | null;
  isLoading: boolean;
} {
  const [engine, setEngine] = useState<Engine | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getEngine().then((e) => {
      if (cancelled) return;
      setEngine(e);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { engine, isLoading };
}
